import { MutedAlert } from '@app/components/MutedAlert';
import { useFragment } from '@app/modules/fragments/screens/view/hooks/useFragment';
import { webApi } from '@mi-front/api-clients';
import { QueryView } from '@mi-front/ui';
import { Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { SocialExportUploadItem } from './SocialExportUploadItem';
import { useFragmentSocialUploadsQuery } from './hooks/useFragmentSocialUploadsQuery';

interface SocialExportPanoramaProps {
  /** When provided, use this instead of running the query (dropdown is rendered by parent). */
  readonly uploadsByPlatform?: Map<string, webApi.FragmentSocialUploadItemDto[]>;
}

/**
 * Social export panorama: one BFF call for all fragment social uploads (raw media + video editor
 * projects). Shows a flat list of upload cards. When uploadsByPlatform is not provided, runs the
 * query and also shows the "Publicar en" dropdown; when provided by parent, dropdown is in the parent header.
 * In-progress uploads are polled individually via getUploadById.
 */
export function SocialExportPanorama({
  uploadsByPlatform: externalUploadsByPlatform,
}: SocialExportPanoramaProps = {}) {
  const { fragment } = useFragment();
  const query = useFragmentSocialUploadsQuery(
    externalUploadsByPlatform === undefined ? fragment.id : undefined,
  );
  const uploadsByPlatform = externalUploadsByPlatform ?? query.data;
  const allUploads = Array.from(uploadsByPlatform?.values() ?? []).flat();

  const content =
    allUploads.length === 0 ? (
      <MutedAlert sx={{ mt: 2 }}>
        <FormattedMessage
          id="fragments.socialExport.panorama.notPublished"
          defaultMessage="El fragmento no fue publicado en redes sociales aún"
        />
      </MutedAlert>
    ) : (
      <Stack spacing={2}>
        {allUploads.map((upload) => (
          <SocialExportUploadItem key={upload.id} upload={upload} />
        ))}
      </Stack>
    );

  if (externalUploadsByPlatform !== undefined) {
    return content;
  }

  return (
    <QueryView query={query} inline>
      {() => content}
    </QueryView>
  );
}
